import { AccredibleObjectWithId } from '@accredible-frontend-v2/models';
import { AccredibleKeyboardFocusService } from '@accredible-frontend-v2/services/keyboard-focus';
import { AccredibleKey } from '@accredible-frontend-v2/utils/key-enum';
import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, skip } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

// TODO(Fred): The accessibility of this component is not well implemented,
//  check how Jira has implemented in order to implement accessibility correctly

@UntilDestroy()
@Component({
  selector: 'accredible-multi-search-filter',
  templateUrl: './multi-search-filter.component.html',
  styleUrls: [`./multi-search-filter.component.scss`],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccredibleMultiSearchFilterComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input()
  dataCy = 'multi-search';
  @Input()
  label: string;
  @Input()
  placeholder = '';
  @Input()
  hint: string;
  @Input()
  optionsLabelKey: string;
  @Input()
  optionsImgKey: string;
  @Input()
  optionsLimit: number;
  @Input()
  usingStaticOptions = false;
  @Input()
  useEllipsisOnOptions = true;
  @Input()
  useMaxHeight = false;
  @Input()
  isInMenu = false;
  @Input()
  hideSearchInput = false;
  @Input()
  ariaLabel: string;
  @Input()
  maxSelect = Number.MAX_SAFE_INTEGER;
  @Output()
  optionsChanged = new EventEmitter<AccredibleObjectWithId[]>();
  @Output()
  searchChanged = new EventEmitter<string>();

  @ViewChild('searchInputContainerElement')
  searchInputContainerElement: ElementRef<HTMLInputElement>;
  @ViewChild('searchInputElement')
  searchInputElement: ElementRef<HTMLInputElement>;
  @ViewChild('searchInputCopyElement')
  searchInputCopyElement: ElementRef<HTMLElement>;
  @ViewChild('scrollContainer')
  scrollContainer: ElementRef<HTMLElement>;

  searchQuery$ = new BehaviorSubject<string>('');

  searchQueryNgModel = '';
  highlightedOption: AccredibleObjectWithId | null;
  // displayedOptions are the ones showing on the list
  displayedOptions: AccredibleObjectWithId[] = [];
  // selectedOptionsIds is used to add a selected class on the HTML
  selectedOptionsIds: unknown[] = [];
  boxDisabled = false;
  inputFocused = false;
  cdkKeyboardFocused = false;
  showTopGradient = false;
  showBottomGradient = false;

  constructor(
    @Inject(DOCUMENT) private readonly _document: Document,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _deviceDetector: DeviceDetectorService,
    private readonly _keyboardFocus: AccredibleKeyboardFocusService,
  ) {}

  private _options: AccredibleObjectWithId[];

  get options(): AccredibleObjectWithId[] {
    return this._options;
  }

  @Input()
  set options(options: AccredibleObjectWithId[]) {
    this._options = options;
    this.displayedOptions = options;
    if (this.highlightedOption) {
      this.highlightedOption = this.displayedOptions.length > 0 ? this.displayedOptions[0] : null;
    }
  }

  // _selectedOptions are the ones showing as chips on the input
  private _selectedOptions: AccredibleObjectWithId[] = [];

  get selectedOptions(): AccredibleObjectWithId[] {
    return this._selectedOptions;
  }

  @Input()
  set selectedOptions(selectedOptions: AccredibleObjectWithId[]) {
    if (!!selectedOptions) {
      this._selectedOptions = selectedOptions;
      this.selectedOptionsIds = selectedOptions.map((o) => o.id);

      this._validateSelectGroupsCheckboxDisabled();
    }
  }

  @HostListener('keydown', ['$event'])
  keyDownEvent(event: KeyboardEvent): void {
    this._onMultipleSearchKeyDown(event);
  }

  ngOnInit(): void {
    this.searchQuery$
      .pipe(
        untilDestroyed(this),
        skip(1),
        debounceTime(this.usingStaticOptions ? 0 : 300),
        distinctUntilChanged(),
      )
      .subscribe((query) => {
        this.searchQueryNgModel = query;

        if (this.usingStaticOptions) {
          this.displayedOptions = this._filterOptionsByQuery(query);
          this._cdr.markForCheck();
        }
        this.searchChanged.emit(query);
      });
    this._validateSelectGroupsCheckboxDisabled();
  }

  ngAfterViewInit(): void {
    this._createInputCopy();
    this._setInputStyles();
    this._keyboardFocus.startFocusMonitoring(this.searchInputElement);
    if (this.isInMenu) {
      // Focus input if menu was opened
      this.searchInputElement.nativeElement.focus();
    }

    // Force onScroll in order to set the correct values for show gradients
    this.scrollContainer && this.onScroll(this.scrollContainer.nativeElement);
  }

  ngOnDestroy(): void {
    this._keyboardFocus.stopFocusMonitoring(this.searchInputElement);
  }

  onSearchQueryChange(query: string): void {
    this._setInputStyles();
    this.searchQuery$.next(query);
  }

  toggleOption(option: AccredibleObjectWithId): void {
    const isOptionSelected = !!this._selectedOptions.find((o) => o.id === option.id);

    if (this.maxSelect <= this.selectedOptionsIds.length && !isOptionSelected) {
      return;
    }

    if (isOptionSelected) {
      this._selectedOptions = this._selectedOptions.filter((o) => o.id !== option.id);
    } else {
      this._selectedOptions = [...this._selectedOptions, option];
    }

    this.selectedOptionsIds = this._selectedOptions.map((o) => o.id);
    this._emitNewOptionsValues();

    this.searchQueryNgModel = '';
    this.searchInputElement.nativeElement.value = '';
    this.searchQuery$.next('');

    if (this._deviceDetector.isDesktop()) {
      // Only focus input if on a desktop device in order to not open the keyboard on mobile/tablet devices
      this.searchInputElement.nativeElement.focus();
    }

    this._validateSelectGroupsCheckboxDisabled();
  }

  onOptionHover(option: AccredibleObjectWithId): void {
    this.highlightedOption = option;
  }

  onRemoveOption(option: AccredibleObjectWithId): void {
    this._selectedOptions = this._selectedOptions.filter((o) => o.id !== option.id);
    this.selectedOptionsIds = this._selectedOptions.map((o) => o.id);
    this._validateSelectGroupsCheckboxDisabled();
    this._emitNewOptionsValues();
  }

  onRemoveAllOptions(): void {
    this._selectedOptions = [];
    this.selectedOptionsIds = [];
    this.boxDisabled = false;
    this.displayedOptions = [...this._options];
    this.searchQuery$.next('');
    this.searchInputElement.nativeElement.value = '';
    this._emitNewOptionsValues();
  }

  onScroll(target: EventTarget): void {
    const [scrollPosition, scrollingContainerHeight, totalHeight, overflow] = [
      (target as HTMLElement).scrollTop,
      (target as HTMLElement).offsetHeight,
      (target as HTMLElement).scrollHeight,
      window.getComputedStyle(target as HTMLElement).overflowY,
    ];

    // Only show gradient if not inside a menu and not using max height
    if (!this.isInMenu && !this.useMaxHeight) {
      this.showTopGradient = overflow !== 'visible' && scrollPosition > 0;
      this.showBottomGradient =
        overflow !== 'visible' && scrollPosition + scrollingContainerHeight < totalHeight;
    }
  }

  onMouseLeaveScrollContainer(): void {
    if (!this.isInMenu) {
      this.highlightedOption = null;
    }
  }

  setInputKeyboardFocus(event: Event, isFocused: boolean): void {
    this.inputFocused = isFocused;
    this.cdkKeyboardFocused = (event.target as HTMLElement).classList.contains(
      'cdk-keyboard-focused',
    );
    !isFocused && this.onMouseLeaveScrollContainer();
  }

  /**
   * Creates a div that has the same style as the input and puts it off screen,
   * so we can then add the input text to this div and see it's width,
   * thus using this value to compute the input width
   * From: https://htmldom.dev/resize-the-width-of-a-text-box-to-fit-its-content-automatically/
   */
  private _createInputCopy(): void {
    // Get the styles
    const styles = window.getComputedStyle(this.searchInputElement.nativeElement);
    const attributes = [
      'fontFamily',
      'fontSize',
      'fontStyle',
      'fontWeight',
      'letterSpacing',
      'textTransform',
      'borderLeftWidth',
      'borderRightWidth',
    ];

    // Copy styles from the input element
    attributes.forEach((attr) => {
      (<any>this.searchInputCopyElement.nativeElement.style)[attr] = (<any>styles)[attr];
    });
  }

  /**
   * Sets the input width according to the text it contains
   * This is done by copying the text to the copy/fake div, and
   * seeing what it's width is, then applying it to the input
   */
  private _setInputStyles(): void {
    // When we set the input width we also want to move the scroll to the top
    if (this.scrollContainer) {
      this.scrollContainer.nativeElement.scrollTop = 0;
    }

    const inputElement = this.searchInputElement.nativeElement;
    const inputString =
      this._selectedOptions.length > 0
        ? inputElement.value
        : inputElement.value || this.placeholder || '';
    this.searchInputCopyElement.nativeElement.innerHTML = inputString.replace(/\s/g, '&' + 'nbsp;');
    // + 2 is in order for the user to be able to see the blinking cursor when input is empty
    inputElement.style.width = `${this.searchInputCopyElement.nativeElement.clientWidth + 2}px`;
  }

  private _onMultipleSearchKeyDown(event: KeyboardEvent): void {
    switch (event.code) {
      case AccredibleKey.ARROW_UP:
      case AccredibleKey.ARROW_DOWN:
        if (this.displayedOptions.length > 0) {
          this._handleArrowKeys(event.code);
        }
        break;
      case AccredibleKey.ENTER:
        this._handleEnterKey();
        break;
      case AccredibleKey.SPACE:
        this._handleSpaceKey(event);
        break;
      case AccredibleKey.BACKSPACE:
        this._handleBackspaceKey();
        break;
    }
  }

  private _handleArrowKeys(code: string): void {
    let highlightedOptionIndex = this.displayedOptions.findIndex(
      (option) => option.id === this.highlightedOption?.id,
    );

    switch (code) {
      case AccredibleKey.ARROW_UP:
        highlightedOptionIndex =
          highlightedOptionIndex === -1 || highlightedOptionIndex > 0
            ? highlightedOptionIndex - 1
            : this.displayedOptions.length - 1;
        break;
      case AccredibleKey.ARROW_DOWN:
        highlightedOptionIndex =
          highlightedOptionIndex === -1 || highlightedOptionIndex < this.displayedOptions.length - 1
            ? highlightedOptionIndex + 1
            : 0;
        break;
    }

    this.highlightedOption = this.displayedOptions[highlightedOptionIndex];
    this._updateScrollPosition(highlightedOptionIndex);
  }

  private _handleEnterKey(): void {
    // If there are options being displayed
    if (this.displayedOptions.length > 0) {
      // Select first option is there's no highlighted option
      this.toggleOption(this.highlightedOption || this.displayedOptions[0]);
    }
    this._emitNewOptionsValues();
  }

  private _handleSpaceKey(event: KeyboardEvent): void {
    if (this.searchQuery$.value === '') {
      this._handleEnterKey();
      event.preventDefault();
    }
    event.stopPropagation();
  }

  private _handleBackspaceKey(): void {
    // If we have selected options and search query is empty, remove last selected option
    if (this._selectedOptions.length > 0 && this.searchQuery$.value === '') {
      this.toggleOption(this._selectedOptions[this._selectedOptions.length - 1]);
    }
  }

  private _emitNewOptionsValues(): void {
    this.optionsChanged.emit(this._selectedOptions);
    this._setInputStyles();
  }

  private _filterOptionsByQuery(query: string): AccredibleObjectWithId[] {
    return this._options.filter(
      (option) =>
        (<string>option[this.optionsLabelKey]).toLowerCase().indexOf(query.toLowerCase()) !== -1,
    );
  }

  /**
   * Update scroll position when the user is using the arrow keys to navigate the options
   */
  private _updateScrollPosition(highlightedOptionIndex: number): void {
    const elementSize = 32; // This value is based on the current design
    const highlightedItem = this.scrollContainer.nativeElement.children[highlightedOptionIndex];

    if (highlightedItem) {
      const highlightedItemTop = (<HTMLElement>highlightedItem).offsetTop;
      const highlightedItemBottom =
        (<HTMLElement>highlightedItem).offsetTop +
        (<HTMLElement>highlightedItem).offsetHeight +
        elementSize;
      const scrollContainerBottom = this.scrollContainer.nativeElement.offsetHeight;
      const scrollPosition = this.scrollContainer.nativeElement.scrollTop;

      if (highlightedItemBottom > scrollContainerBottom + scrollPosition) {
        this.scrollContainer.nativeElement.scrollTop =
          highlightedItemBottom - scrollContainerBottom;
      } else if (highlightedItemTop < scrollPosition) {
        this.scrollContainer.nativeElement.scrollTop = highlightedItemTop;
      }
    }
  }

  private _validateSelectGroupsCheckboxDisabled(): void {
    this.boxDisabled = this.maxSelect === this.selectedOptionsIds.length;
  }
}
